import React, { useState, useEffect } from 'react';
import { TextField, Chip, Box, Typography } from '@mui/material';

/**
 * Component pentru input de email-uri multiple afișate ca chips
 * @param {string} value - valoarea curentă (string cu emailuri separate prin ;)
 * @param {function} onChange - callback când se schimbă valoarea
 * @param {string} label - label-ul câmpului
 * @param {boolean} disabled - dacă câmpul este dezactivat
 */
function EmailChipsInput({ value, onChange, label = 'Adrese email multiple', disabled = false }) {
  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  // Parsează valoarea inițială din string în array
  useEffect(() => {
    if (value) {
      const parsed = value.split(';').map(e => e.trim()).filter(e => e);
      setEmails(parsed);
    } else {
      setEmails([]);
    }
  }, [value]);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const updateParent = (newEmails) => {
    const stringValue = newEmails.join('; ');
    onChange(stringValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      addEmail();
    }
    if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      const newEmails = emails.slice(0, -1);
      setEmails(newEmails);
      updateParent(newEmails);
    }
  };

  const addEmail = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    if (!isValidEmail(trimmed)) {
      setError('Adresă de email invalidă');
      return;
    }

    if (emails.includes(trimmed)) {
      setError('Această adresă există deja');
      return;
    }

    const newEmails = [...emails, trimmed];
    setEmails(newEmails);
    updateParent(newEmails);
    setInputValue('');
    setError('');
  };

  const handleDelete = (emailToDelete) => {
    const newEmails = emails.filter(email => email !== emailToDelete);
    setEmails(newEmails);
    updateParent(newEmails);
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmail();
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, minHeight: emails.length > 0 ? '32px' : 0 }}>
        {emails.map((email, index) => (
          <Chip
            key={index}
            label={email}
            size="small"
            onDelete={disabled ? undefined : () => handleDelete(email)}
            color="primary"
            variant="outlined"
          />
        ))}
      </Box>
      <TextField
        fullWidth
        label={label}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setError('');
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={disabled}
        error={!!error}
        helperText={error || ''}
        margin="dense"
        size="small"
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Puteți introduce mai multe adrese de email. Apăsați Enter sau virgulă după fiecare adresă.
      </Typography>
    </Box>
  );
}

export default EmailChipsInput;
